import { usaStates } from "typed-usa-states"

import {
    AvailableInsuranceType,
    AvailablePlanType,
    COMMON_COVERAGE_CONFIG,
} from "@common-shared/config/coverage.config"
import {
    FeatureFlag,
    FeatureFlagsExistenceLogicalOperator,
} from "@common-shared/config/featureFlags.config"
import {
    COMMON_ONE_VERIFY_COVERAGE_CONFIG,
    CoverageBreakdownSection,
    QuotaUsageType,
} from "@common-shared/config/oneVerify/oneVerifyCoverage.config"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config"

import { ICoverageBreakdownData } from "../types/data.types"
import type { ICoverageBreakdownSectionConfig } from "../types/objects.types"
import { TOOLTIPS_CONFIG } from "./tooltips.config"

import sectionFinancialsIcon from "@front-shared/assets/images/icons/coverage-section-financials.svg"
import sectionFlagsIcon from "@front-shared/assets/images/icons/coverage-section-flags.svg"
import sectionGeneralIcon from "@front-shared/assets/images/icons/coverage-section-general.svg"
import sectionTelehealthIcon from "@front-shared/assets/images/icons/coverage-section-laptop-medical.svg"
import sectionObligationIcon from "@front-shared/assets/images/icons/coverage-section-obligation.svg"
import sectionSpecialtySpecificIcon from "@front-shared/assets/images/icons/coverage-section-specialty-specific.svg"

export const COVERAGE_CONFIG = {
    ...COMMON_COVERAGE_CONFIG,
    ...COMMON_ONE_VERIFY_COVERAGE_CONFIG,

    breakdownPlaceholderLabel: "__PLACEHOLDER__",

    usageQuota: {
        inProgressQuotaColor: NIRVANA_COLORS.brand.orange2,

        get ignoredTypes(): QuotaUsageType[] {
            return [QuotaUsageType.MEDICAID]
        },

        get usageTypeConfig(): Record<
            QuotaUsageType,
            {
                label: string
                color: string
                tooltip?: string
                featureFlags?: FeatureFlag[]
                featureFlagExistenceOperator?: FeatureFlagsExistenceLogicalOperator
            }
        > {
            return {
                [QuotaUsageType.GENERAL]: {
                    label: "Standard",
                    color: NIRVANA_COLORS.brand.primary,
                    featureFlags: [
                        FeatureFlag.ONE_VERIFY_STANDARD_MANUAL_CHECK,
                        FeatureFlag.ONE_VERIFY_STANDARD_BULK_CHECKS,
                    ],
                    featureFlagExistenceOperator:
                        FeatureFlagsExistenceLogicalOperator.OR,
                },
                [QuotaUsageType.DISCOVER]: {
                    label: "Discover",
                    color: NIRVANA_COLORS.brand.vibrantPurple,
                    tooltip: "Checks run without Member ID – Known Payer",
                    featureFlags: [
                        FeatureFlag.ONE_VERIFY_DISCOVER_MANUAL_CHECK,
                        FeatureFlag.ONE_VERIFY_DISCOVER_BULK_CHECKS,
                    ],
                    featureFlagExistenceOperator:
                        FeatureFlagsExistenceLogicalOperator.OR,
                },
                [QuotaUsageType.SMART_SCAN]: {
                    label: "Smart Scan",
                    color: NIRVANA_COLORS.brand.white,
                    tooltip: "Checks run without Member ID – Unknown Payer",
                    featureFlags: [
                        FeatureFlag.ONE_VERIFY_SCAN_MANUAL_CHECK,
                        FeatureFlag.ONE_VERIFY_SCAN_BULK_CHECKS,
                    ],
                    featureFlagExistenceOperator:
                        FeatureFlagsExistenceLogicalOperator.OR,
                },
                [QuotaUsageType.MEDICAID]: {
                    label: "Medicaid",
                    color: NIRVANA_COLORS.brand.lilacLight,
                    featureFlags: [
                        FeatureFlag.ONE_VERIFY_MEDICAID_MANUAL_CHECK,
                        FeatureFlag.ONE_VERIFY_MEDICAID_BULK_CHECKS,
                    ],
                    featureFlagExistenceOperator:
                        FeatureFlagsExistenceLogicalOperator.OR,
                },
                [QuotaUsageType.AGENTIC_PORTAL]: {
                    label: "Agentic Portal",
                    color: NIRVANA_COLORS.brand.lilac,
                    featureFlags: [
                        FeatureFlag.ONE_VERIFY_AGENTIC_PORTAL_MANUAL_CHECK,
                        FeatureFlag.ONE_VERIFY_AGENTIC_PORTAL_BULK_CHECKS,
                    ],
                    featureFlagExistenceOperator:
                        FeatureFlagsExistenceLogicalOperator.OR,
                },
                [QuotaUsageType.AGENTIC_PHONE]: {
                    label: "Agentic Phone",
                    color: NIRVANA_COLORS.brand.greenLight,
                    featureFlags: [
                        FeatureFlag.ONE_VERIFY_AGENTIC_PHONE_MANUAL_CHECK,
                        FeatureFlag.ONE_VERIFY_AGENTIC_PHONE_BULK_CHECKS,
                    ],
                    featureFlagExistenceOperator:
                        FeatureFlagsExistenceLogicalOperator.OR,
                },
            }
        },
    },

    // Some states require member ID or gender for Medicaid checks
    get medicaidStatesRequireGenderOrMemberID(): string[] {
        return ["FL", "GA", "KY", "WI", "MA", "HI", "WY"]
    },

    // Some states require member ID for Medicaid checks
    get medicaidStatesRequireMemberID(): string[] {
        return ["AK", "CT", "ME", "MD", "NY", "RI", "SD", "UT", "VT", "WV"]
    },

    // Some states do not support Medicaid checks, particularly territories
    get medicaidStatesNotSupported(): string[] {
        return ["CA", "MT", "AS", "GU", "MP", "PR", "VI"]
    },

    // Inverse of medicaidStatesNotSupported, helpful for certain use cases
    get medicaidStatesSupported(): string[] {
        return usaStates
            .map(state => state.abbreviation)
            .filter(state => !this.medicaidStatesNotSupported.includes(state))
    },

    get coverageBreakdownSections(): Record<
        CoverageBreakdownSection,
        ICoverageBreakdownSectionConfig
    > {
        return {
            [CoverageBreakdownSection.FLAGS]: {
                title: "Alerts",
                withSectionDividerLine: true,
                icon: sectionFlagsIcon,
            },
            [CoverageBreakdownSection.POLICY_GENERAL]: {
                title: "General Information",
                withSectionDividerLine: true,
                icon: sectionGeneralIcon,
                brokenInColumns: true,
            },
            [CoverageBreakdownSection.POLICY_THIRD_PARTY]: {
                title: "Third Party Administrator",
                bigFont: true,
                withSectionLeftBorder: true,
                singleColumn: true,
            },
            [CoverageBreakdownSection.POLICY_ADDITIONAL_POLICY]: {
                title: "Additional Policy",
                bigFont: true,
                withSectionLeftBorder: true,
                tooltip: TOOLTIPS_CONFIG.coverageBreakdown.additionalPolicy,
                singleColumn: true,
            },
            [CoverageBreakdownSection.POLICY_FINANCIALS]: {
                title: "Financials",
                withSectionDividerLine: true,
                icon: sectionFinancialsIcon,
                brokenInColumns: true,
            },
            [CoverageBreakdownSection.POLICY_OBLIGATION]: {
                title: "Obligation Details",
                withSectionDividerLine: true,
                icon: sectionObligationIcon,
                brokenInColumns: true,
            },
            [CoverageBreakdownSection.POLICY_MEDICARE_ADVANTAGE]: {
                title: "Medicare Advantage",
                bigFont: true,
                withSectionLeftBorder: true,
                tooltip: TOOLTIPS_CONFIG.coverageBreakdown.medicareAdvantage,
                singleColumn: true,
            },
            [CoverageBreakdownSection.SPECIALTY_SPECIFIC]: {
                title: "Specialty Specific",
                withSectionDividerLine: true,
                icon: sectionSpecialtySpecificIcon,
                brokenInColumns: true,
            },
            [CoverageBreakdownSection.TELEHEALTH]: {
                title: "Telehealth",
                withSectionDividerLine: true,
                icon: sectionTelehealthIcon,
                brokenInColumns: true,
            },
            [CoverageBreakdownSection.PRIMARY_CARE_PHYSICIAN]: {
                title: "Primary Care Physician",
                withSectionDividerLine: true,
                brokenInColumns: true,
            },
        }
    },

    get defaultDemographicBreakdownItems(): ICoverageBreakdownData[] {
        return [
            {
                label: "Member Name",
                value: COVERAGE_CONFIG.defaultFallbackCoverageDataValueNotProvided,
            },
            {
                label: "DOB",
                value: COVERAGE_CONFIG.defaultFallbackCoverageDataValueUnknown,
            },
            {
                label: "Address",
                value: COVERAGE_CONFIG.defaultFallbackCoverageDataValueUnknown,
            },
            {
                label: "Gender",
                value: COVERAGE_CONFIG.defaultFallbackCoverageDataValueUnknown,
            },
        ]
    },

    get defaultResetBenefitsThirdPartyAdministratorItems(): ICoverageBreakdownData[] {
        return [
            {
                label: "Name",
                value: COVERAGE_CONFIG.defaultFallbackCoverageDataValueNA,
                valueDifference:
                    COVERAGE_CONFIG.defaultFallbackCoverageDataValueNA,
            },
            {
                label: "Type",
                value: COVERAGE_CONFIG.defaultFallbackCoverageDataValueNA,
                valueDifference:
                    COVERAGE_CONFIG.defaultFallbackCoverageDataValueNA,
            },
        ]
    },

    get defaultResetBenefitsAdditionalPolicyItems(): ICoverageBreakdownData[] {
        return [
            {
                label: "Payer",
                value: COVERAGE_CONFIG.defaultFallbackCoverageDataValueNA,
                valueDifference:
                    COVERAGE_CONFIG.defaultFallbackCoverageDataValueNA,
            },
            {
                label: "Member ID",
                value: COVERAGE_CONFIG.defaultFallbackCoverageDataValueNA,
                valueDifference:
                    COVERAGE_CONFIG.defaultFallbackCoverageDataValueNA,
            },
            {
                label: "Policy Type",
                value: COVERAGE_CONFIG.defaultFallbackCoverageDataValueNA,
                valueDifference:
                    COVERAGE_CONFIG.defaultFallbackCoverageDataValueNA,
            },
            {
                label: "Plan Type",
                value: COVERAGE_CONFIG.defaultFallbackCoverageDataValueNA,
                valueDifference:
                    COVERAGE_CONFIG.defaultFallbackCoverageDataValueNA,
            },
        ]
    },

    get unsupportedInsuranceTypes(): AvailableInsuranceType[] {
        return [
            AvailableInsuranceType.INDEMNITY,
            AvailableInsuranceType.HMO_POS,
            AvailableInsuranceType.HDHP,
        ]
    },

    get unsupportedPlanTypes(): AvailablePlanType[] {
        return [
            AvailablePlanType.EMPLOYEE_ASSISTANCE_PROGRAM,
            AvailablePlanType.SUPPLEMENTAL_MEDICARE,
            AvailablePlanType.VETERANS_AFFAIRS,
        ]
    },
}
